import { CompozzError, isCompozzError } from "@dto/CompozzError";
import { EditRightAccess, ObjectRightType, ViewRightType, hasGlobalRightAccess } from "@dto/Users";
import Field, { AggregationType, FieldType, TYPE_ITEMS_FIELD_KEY, TYPE_ITEMS_PERIOD_TYPE, TYPE_ITEMS_RELATION_KEY, fieldToOutputType } from "@dto/fields";
import { Period, PeriodTypeDay, PeriodTypeYear } from "@dto/period";
import { Direction, FieldColumn, Item, ItemKey, ItemValue, Relation, areItemsEqual, isLinkColumn } from "@dto/records";
import { copyAll, getSelectedCells, pasteAll } from "@utils/copy";
import { addLink, deleteLinks, fillPivot, getLink, getRecordKeys, updateItem, updateItemValues } from "@utils/data";
import { hasFilter } from "@utils/filters";
import { fieldToPath, getFieldByPathKeys, getRelationDirection, pathKey } from "@utils/model";
import { toastError } from "@utils/toasts";
import { TW_GridCell, TW_GridCell_SelectionHandle, getWsButtonStyle } from "@utils/twClasses";
import { containsPosition, downPosition, getLine, inputRefToValue, isCompatible, isFormulaError, isValidatable, leftPosition, nextPosition, posToString, prevPosition, rightPosition, samePosition, upPosition } from "@utils/types";
import { useConfirmationDialog } from "@comps/ui/confirmation-dialog";
import { CSSProperties, RefObject, memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { translate } from "src/i18n/i18n";
import { useDataStore, useModelStore, useUserStore } from "src/store";
import { CellFocusContext } from "./CellFocusContext";
import { CheckboxCell } from "./CheckboxCell";
import { FormulaErrorCell } from "./FormulaErrorCell";
import { ChoiceCell } from "./ChoiceCell";
import { CurrencyCell } from "./CurrencyCell";
import { DateCell } from "./DateCell";
import { DecimalCell } from "./DecimalCell";
import { LinkCell } from "./LinkCell";
import { isReadOnlyLinkChainColumn, linkedRecordKey, linkSourceKey } from "./linkChain";
import { NumberCell } from "./NumberCell";
import { PercentageCell } from "./PercentageCell";
import { PeriodCell } from "./PeriodCell";
import { TextAreaCell } from "./TextAreaCell";
import { TextCell } from "./TextCell";
import { UrlCell } from "./UrlCell";
import { UserCell } from "./UserCell";
import { ButtonCell } from "./ButtonCell";


export const NEW_ROW_ID = "new";

export interface CellPosition {
    rowId: string;
    column: number;
}

export function copyPos(pos: CellPosition | null): CellPosition | null {
    if (!pos) {
        return null;
    }
    return { rowId: pos.rowId, column: pos.column };
}

export function copyPosArray(pos: CellPosition[] | null): CellPosition[] | null {
    if (!pos) {
        return null;
    }
    return pos.map(p => ({ rowId: p.rowId, column: p.column }));
}

// Function to get the last selected cell based on anchor cell position
export function getLastSelectionCell(selections: CellPosition[], anchorCell: CellPosition | null, rowIds: string[]): CellPosition {
    if (selections.length === 0) {
        // Fallback: return anchor cell if no selections, or a default position
        return anchorCell || { rowId: "", column: 0 };
    }
    if (!anchorCell) {
        // Fallback to last position if no anchor
        return selections[selections.length - 1];
    }
    const firstSelection = selections[0];
    const lastSelection = selections[selections.length - 1];
    const firstSelectionRowIndex = rowIds.indexOf(firstSelection.rowId);
    const lastSelectionRowIndex = rowIds.indexOf(lastSelection.rowId);

    const anchorRowIndex = rowIds.indexOf(anchorCell.rowId);
    if (anchorRowIndex === -1) {
        return anchorCell;
    }

    const anchorCol = anchorCell.column;

    if (anchorRowIndex !== firstSelectionRowIndex && anchorRowIndex !== lastSelectionRowIndex) {
        return anchorCell;
    }
    if (anchorCol !== firstSelection.column && anchorCol !== lastSelection.column) {
        return anchorCell;
    }

    let lastSelRowId: string;
    if (anchorRowIndex === firstSelectionRowIndex) {
        lastSelRowId = lastSelection.rowId;
    } else {
        lastSelRowId = firstSelection.rowId;
    }

    let lastSelCol: number;
    if (anchorCol === firstSelection.column) {
        lastSelCol = lastSelection.column;
    } else {
        lastSelCol = firstSelection.column;
    }

    return { rowId: lastSelRowId, column: lastSelCol };
}

export interface StyleProps {
    style: CSSProperties;
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
    // onClick?: () => void;
    onBlur?: () => void;
    tabIndex?: number;
    // onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
    onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement> | React.KeyboardEvent<HTMLInputElement>) => void;
    ref?: (el: HTMLDivElement | HTMLButtonElement | null) => void;
}

export interface CellProps {
    pathKey: string | null;
    field: Field;
    item: Item;
    position: CellPosition | null;
    updateValue: (item: Item, value: any) => void;
    isEditing: boolean;
    isFocused: boolean;
    inputRef: RefObject<HTMLInputElement>
    blurTriggered: number | null;
    pKey: string | null;
    currentAggregation: AggregationType;
    noDataFetch?: boolean;
}

export interface GridCellProps {
    fieldColumn: FieldColumn;
    item: Item;
    position: CellPosition | null;
    noDataFetch?: boolean;
    onSelectionChange?: (position: CellPosition, shiftKey: boolean, ctrlKey: boolean) => void;
    onSelectionHandleDragStart?: (position: CellPosition) => void;
    onSelectionHandleDragOver?: (position: CellPosition) => void;
    onContextMenuOpen?: (x: number, y: number, canPaste: boolean, pasteAction: () => void, canDelete: boolean, deleteAction: () => void) => void;
}

type SelectionHandleCorner = "top-left" | "top-right" | "bottom-left" | "bottom-right";
const TOP_LEFT: SelectionHandleCorner = "top-left";
const TOP_RIGHT: SelectionHandleCorner = "top-right";
const BOTTOM_LEFT: SelectionHandleCorner = "bottom-left";
const BOTTOM_RIGHT: SelectionHandleCorner = "bottom-right";

export const GridCell = memo(function GridCell({ fieldColumn, item, position, noDataFetch = false, onSelectionChange, onSelectionHandleDragStart, onSelectionHandleDragOver, onContextMenuOpen }: GridCellProps) {

    const workspace = useModelStore(state => state.selectedWorkspace);
    const fieldColumns = useDataStore(state => state.fieldColumns);
    const [localItem, setLocalItem] = useState(item);
    const colNumber = useDataStore(state => state.colNumber);
    const fetchData = useDataStore(state => state.fetchData);
    const loadData = useDataStore(state => state.loadData);
    const stopLoadingData = useDataStore(state => state.stopLoadingData);
    const setRowSelection = useDataStore(state => state.setRowSelection);
    const updateData = useDataStore(state => state.updateDataOnly);
    const applyItemValues = useDataStore(state => state.applyItemValues);
    const applyCellValues = useDataStore(state => state.applyCellValues);
    const wsColor = useModelStore(state => state.wsColor);
    const wsLightColor = useModelStore(state => state.wsLightColor);
    const focusStyle = useMemo(() => getWsButtonStyle(wsLightColor, wsColor), [wsLightColor, wsColor]);
    const normalStyle = useMemo(() => getWsButtonStyle('transparent'), []);
    const cellRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const selectedObject = useModelStore(state => state.selectedObjectInfos);
    const selectedView = useModelStore(state => state.selectedView);
    const focus = useDataStore(state => state.focus);
    const edit = useDataStore(state => state.edit);
    const selectedObjectInfos = useModelStore(state => state.selectedObjectInfos);
    const relationByKeys = useModelStore(state => state.relationByKeys);
    const fieldByKeys = useModelStore(state => state.fieldByKeys);
    const fieldByPathKeys = useMemo(() =>
        getFieldByPathKeys(selectedObjectInfos, selectedObjectInfos?.fields ?? [], relationByKeys, fieldByKeys),
        [selectedObjectInfos, relationByKeys, fieldByKeys]
    );
    const { t } = useTranslation();
    const userRole = useModelStore(state => state.userRole);
    const user = useUserStore(state => state.user);
    const hasEditAccessOnObject = hasGlobalRightAccess(user, userRole, ObjectRightType, EditRightAccess, selectedObjectInfos?.object?._key);
    const hasEditAccessOnView = hasGlobalRightAccess(user, userRole, ViewRightType, EditRightAccess, selectedView?._key);
    const hasEditAccess = hasEditAccessOnObject && hasEditAccessOnView;
    const field = fieldColumn?.srcField || fieldColumn?.field;
    const isReadOnlyLinkChain = useMemo(() =>
        isReadOnlyLinkChainColumn(fieldColumn, relationByKeys, fieldByKeys),
        [fieldColumn, relationByKeys, fieldByKeys]
    );
    const readOnly = item.readOnly || !hasEditAccess || isReadOnlyLinkChain;
    const fetchLine = useDataStore(state => state.fetchLine);
    const confirmDialog = useConfirmationDialog();
    const wsDarkColor = useModelStore(state => state.wsDarkColor);

    const [isHovered, setIsHovered] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [showSelectionHandle, setShowSelectionHandle] = useState(false);
    const [selectionHandleCorner, setSelectionHandleCorner] = useState<SelectionHandleCorner>(BOTTOM_RIGHT);
    const [blurTriggered, setBlurTriggered] = useState<number | null>(null);

    const divKey = useMemo(() => selectedObject?.object._key + "_" + selectedView?._key + "_" + position?.rowId + "_" + position?.column, [selectedObject, selectedView, position]);

    const pKey = useMemo(() => {

        if (!selectedObjectInfos?.object._key || !selectedView) {
            return null;
        }

        const path = fieldToPath(selectedObjectInfos?.object._key, relationByKeys, fieldByKeys, field);
        if (path) {
            return pathKey(path);
        }
        return null;
    }, [selectedObjectInfos, relationByKeys, fieldByKeys, field]);

    let currentAggregation: AggregationType = field.aggregation ?? AggregationType.NO_AGGR;
    if (pKey && selectedView?.aggregations?.get(pKey)) {
        currentAggregation = selectedView?.aggregations?.get(pKey)!;
    }

    // A checkbox is toggled in place by CheckboxCell (Enter, click): it has no
    // editor, so the cell never enters the edit mode, where the arrow keys are
    // left to the (missing) editor.
    const isCheckbox = fieldToOutputType(field, currentAggregation, fieldByKeys) === FieldType.Boolean;


    const selectionHandleStyle = useMemo(() => {
        const baseStyle: CSSProperties = {
            backgroundColor: wsColor,
        };

        switch (selectionHandleCorner) {
            case TOP_LEFT:
                return { ...baseStyle, top: "-0.25rem", left: "-0.25rem" };
            case TOP_RIGHT:
                return { ...baseStyle, top: "-0.25rem", right: "-0.25rem" };
            case BOTTOM_LEFT:
                return { ...baseStyle, bottom: "-0.25rem", left: "-0.25rem" };
            default:
                return { ...baseStyle, bottom: "-0.25rem", right: "-0.25rem" };
        }
    }, [selectionHandleCorner, wsColor]);

    const cellFocusStore = useContext(CellFocusContext);
    const posKey = position ? `${position.rowId}-${position.column}` : null;

    useEffect(() => {
        if (!cellFocusStore || !posKey || !position) return;

        // Initialize from current Zustand state on mount
        const { editingPos, focusPos } = useDataStore.getState();
        if (samePosition(editingPos, position)) {
            setIsEditing(true);
        } else if (containsPosition(focusPos, position)) {
            setIsFocused(true);
        }

        // The store immediately calls the callback with lastParams if available (virtualization)
        return cellFocusStore.subscribe(posKey, ({ isFocused, isEditing, showSelectionHandle, handleCorner, focusDom }) => {
            setIsFocused(isFocused);
            setIsEditing(isEditing);
            setShowSelectionHandle(showSelectionHandle);
            setSelectionHandleCorner(handleCorner as SelectionHandleCorner);
            if (focusDom) cellRef.current?.focus();
        });
    }, [cellFocusStore, posKey]);

    useEffect(() => {
        if (!areItemsEqual(localItem, item)) {
            setLocalItem(item);
        }
    }, [item]);

    const onHover = useCallback(() => {
        setIsHovered(true);
    }, [setIsHovered]);

    const onHoverOut = useCallback(() => {
        setIsHovered(false);
    }, [setIsHovered]);

    const computeStyle = useCallback(function computeStyle(_hover: boolean, focusedOrEditing: boolean) {
        if (focusedOrEditing) {
            return focusStyle;
        } else {
            return normalStyle;
        }
    }, [focusStyle, normalStyle]);

    const styleProps: StyleProps = useMemo(() => {
        return {
            style: computeStyle(isHovered, isFocused || isEditing),
            onMouseEnter: () => {
                onHover();
                if (position) {
                    onSelectionHandleDragOver?.(position);
                }
            },
            onMouseLeave: onHoverOut,
            // onKeyDown: onKeyDown,
            // ref: cellRef
        }
    }, [computeStyle, isHovered, isFocused, isEditing, onHover, onHoverOut, onSelectionHandleDragOver, position]
    );

    const updateState = useCallback(async (newItem: Item) => {
        if (!position) {
            return;
        }

        const data = useDataStore.getState().data;
        const line = getLine(data, position.rowId);
        if (line) {
            line.items[position.column] = newItem;
            updateData(data);
        }
        setLocalItem(newItem);
    }, [position]);

    // applyOptimisticValues updates the grid store IMMUTABLY for a batch of cells
    // (found by item key): the store rebuilds the changed lines/items with new
    // references, grouped children included, so react-table re-derives the rows
    // and every affected cell re-renders. This is the optimistic display for a
    // batched (single-request) paste/fill. Mutating in place would NOT re-render:
    // the cell item is memoized on line.items by ref.
    const applyOptimisticValues = useCallback((values: ItemValue[]) => {
        applyCellValues(values.map(v => ({ ...v.key, value: v.value })));
    }, [applyCellValues]);

    // updateLink sets (value = the item of the target record) or clears (value =
    // null) the link of a cell which has one. The cell is identified by its item,
    // its link field and its row: the multi-cell clear passes the ones of each
    // cell of the selection, not the focused one.
    const updateLink = useCallback(async function (item: Item, value: any, linkField: Field = field, rowId: string | undefined = position?.rowId): Promise<void> {
        if (!workspace || !selectedObjectInfos?.object || readOnly) {
            return;
        }
        // A link chain item does not carry the row record: it is read from the line.
        const metaLine = rowId !== undefined ? useDataStore.getState().metaLines.get(rowId) : undefined;
        const rowRecordKey = metaLine ? getRecordKeys(metaLine, selectedObjectInfos.object._key, true)[0] : undefined;
        const sourceKey = linkSourceKey(item, linkField, fieldByKeys, selectedObjectInfos.object._key, rowRecordKey);
        if (!sourceKey?.recordKey || !linkedRecordKey(item, linkField, fieldByKeys)) {
            return;
        }
        const relationKey = linkField.typeItems?.get(TYPE_ITEMS_RELATION_KEY);
        const relation = relationByKeys?.get(relationKey);
        let direction: Direction = getRelationDirection(relation, selectedObjectInfos?.object._key);
        if (value) {
            const destRecordKey = linkedRecordKey(value as Item, linkField, fieldByKeys);
            if (destRecordKey && relationKey) {
                const response = await addLink(workspace._key, selectedObjectInfos.object._key, relationKey, sourceKey.recordKey, destRecordKey, direction);
                if (response) {
                    if (isCompozzError(response)) {
                        const cError = response as CompozzError;
                        if (cError.i18n) {
                            toastError(translate(t, cError.i18n, cError.params));
                        } else {
                            toastError(cError.message);
                        }
                    }
                }
            }
        } else {
            const response = await getLink(workspace._key, sourceKey.objectKey, relationKey, sourceKey.recordKey, direction);
            if (response) {
                const links = response as Relation[];
                const deleteResponse = await deleteLinks(workspace._key, sourceKey.objectKey, sourceKey.recordKey, relationKey, direction, links.map(link => link._key));
                if (deleteResponse) {
                    if (isCompozzError(deleteResponse)) {
                        const cError = deleteResponse as CompozzError;
                        if (cError.i18n) {
                            toastError(translate(t, cError.i18n, cError.params));
                        } else {
                            toastError(cError.message);
                        }
                    }
                }
            }
        }
    }, [updateState, workspace, selectedObjectInfos, field, relationByKeys, fieldByKeys, position, readOnly]);

    const pathsInDependencies = useMemo(() => {
        const result = new Set<string>();
        fieldColumns.forEach(fieldColumn => {
            if (fieldColumn.dependencies) {
                fieldColumn.dependencies.forEach(pathKey => result.add(pathKey));
            }
        });
        return Array.from(result);
    }, [fieldColumns]);


    const afterUpdateValue = useCallback(function (newItem: Item) {

        // update the view if:
        // - the view is sorted or grouped by this field
        // - the view has a filter on this field
        // - the view has a formula on this field

        if (!workspace || !selectedObjectInfos?.object) {
            return;
        }
        const path = fieldToPath(selectedObjectInfos?.object._key, relationByKeys, fieldByKeys, field);
        let fieldPathKey = null;
        if (path) {
            fieldPathKey = pathKey(path);
        }
        const metalines = useDataStore.getState().metaLines;
        const metaLine = metalines.get(position!.rowId);

        if ((selectedView?.groups?.some(group => fieldByPathKeys.get(group.pathKey)?._key === field._key) ||
            selectedView?.sortings?.some(sorting => fieldByPathKeys.get(sorting.pathKey)?._key === field._key) ||
            hasFilter(selectedView?.filter, field._key, fieldByPathKeys) ||
            pathsInDependencies.includes(fieldPathKey ?? "")) && !noDataFetch) {
            fetchData().then(() => {
                cellFocusStore?.triggerRefocus();
            });
            setRowSelection({});
        } else if (metaLine && metaLine.id === NEW_ROW_ID) {
            let recordKeys = getRecordKeys(metaLine, selectedObjectInfos.object._key);
            const newItemKey = newItem.key.recordKey;
            if (!recordKeys.includes(newItemKey)) {
                recordKeys.push(newItemKey);
            }
            if (recordKeys.length > 0) {
                fetchLine(NEW_ROW_ID, recordKeys, selectedView);
            }
        }
    }, [selectedObjectInfos, relationByKeys, fieldByKeys, field, noDataFetch, cellFocusStore]);

    const updateValue = useCallback(async function (item: Item, value: any): Promise<void> {
        if (!workspace || !selectedObjectInfos?.object || readOnly) {
            return;
        }
        const metalines = useDataStore.getState().metaLines;
        // A link chain cell may point to a record without displayed value (no item key)
        const hasRecord = field.type === FieldType.Link ? !!linkedRecordKey(item, field, fieldByKeys) : !!item.key?.recordKey;
        if (hasRecord) {
            if (field.type === FieldType.Link) {
                await updateLink(item, value);
                setIsHovered(false);
                afterUpdateValue(item);
            } else {

                if (value && typeof value === "string" && !isCompatible(value, field, selectedView?.formats?.get(pKey ?? "") ?? field?.format)) {
                    return;
                }

                updateState({ ...item, value: value });

                let itemKeys: ItemKey[] = [item.key];
                const hasPivot = selectedView?.pivot?.pivotPathKey;
                if (hasPivot && !fieldColumn.isPivot) {
                    const metaLine = metalines.get(position!.rowId);
                    if (metaLine && metaLine.line.items.length > 0) {
                        const lineRecordKeys = getRecordKeys(metaLine, selectedObjectInfos.object._key);
                        if (lineRecordKeys.length > 0) {
                            itemKeys = lineRecordKeys.map(key => ({ recordKey: key, objectKey: item.key.objectKey, fieldKey: item.key.fieldKey }));
                        }
                    }
                }

                const response = await updateItem(itemKeys, value);
                if (response) {
                    if (isCompozzError(response)) {
                        const cError = response as CompozzError;
                        if (cError.i18n) {
                            toastError(translate(t, cError.i18n, cError.params));
                        } else {
                            toastError(cError.message);
                        }
                    }
                } else {
                    setIsHovered(false);
                    afterUpdateValue(item);
                }
            }

        } else {
            await new Promise<void>((resolve) => {
                const onUpdate = (newItem: Item) => {
                    updateState(newItem);
                    afterUpdateValue(newItem);
                    resolve();
                }
                fillPivot(workspace._key, position, fieldColumn, selectedView!, item, value, metalines, fieldByPathKeys, selectedObjectInfos, t, onUpdate);
            });
        }
    }, [updateState, updateLink, selectedObjectInfos, relationByKeys, fieldByKeys, field]);

    const updateSelectedValues = useCallback(async function (value: any): Promise<void> {
        if (!workspace || !selectedObjectInfos?.object || readOnly) {
            return;
        }
        const cellsByRow = getSelectedCells();
        const promises: Promise<unknown>[] = [];
        // Scalar cells → one atomic request (one undo op); links keep per-cell path.
        const batchedValues: ItemValue[] = [];
        // Link cells cleared per-cell: their item.key points to the destination
        // record (shared across rows), so we clear them optimistically by item
        // reference to update the line instantly without waiting for fetchData.
        const linkItems: Item[] = [];
        cellsByRow.forEach((rowCells: Map<number, { item: Item; fieldColumn: FieldColumn }>, rowId: string) => {
            rowCells.forEach((cell: { item: Item; fieldColumn: FieldColumn }) => {
                // Read-only cells (formulas, aggregations, relation paths...) are
                // left untouched: their item key may be a linked record.
                if (cell.item.readOnly || isReadOnlyLinkChainColumn(cell.fieldColumn, relationByKeys, fieldByKeys)) {
                    return;
                }
                if (isLinkColumn(cell.fieldColumn)) {
                    // Each link cell is cleared on its own relation, whatever the focused cell
                    const linkField = cell.fieldColumn.srcField ?? cell.fieldColumn.field;
                    if (linkedRecordKey(cell.item, linkField, fieldByKeys)) {
                        linkItems.push(cell.item);
                        promises.push(updateLink(cell.item, value, linkField, rowId));
                    }
                } else if (cell.item.key?.recordKey) {
                    batchedValues.push({ key: cell.item.key, value });
                }
            });
        });
        if (batchedValues.length > 0) {
            applyOptimisticValues(batchedValues);
            promises.push(updateItemValues(batchedValues, selectedObjectInfos?.object._key));
        }
        if (linkItems.length > 0) {
            applyItemValues(linkItems, value);
        }
        await Promise.all(promises);
        if (cellsByRow.size > 0) {
            await fetchData();
        }
    }, [updateLink, selectedObjectInfos, relationByKeys, fieldByKeys, applyOptimisticValues, applyItemValues]);

    const onClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
        if (!position) {
            return;
        }
        if (!isEditing && !isFocused) {
            event.stopPropagation();
            event.preventDefault();

            if (onSelectionChange) {
                onSelectionChange(position, event.shiftKey, event.ctrlKey || event.metaKey);
            }
        } else if (!isEditing && isFocused) {
            event.stopPropagation();
            event.preventDefault();

            const focusPos = useDataStore.getState().focusPos;
            if (focusPos && focusPos.length && focusPos.length > 1 && onSelectionChange) {
                onSelectionChange(position, event.shiftKey, event.ctrlKey || event.metaKey);
            } else if (!readOnly && !isCheckbox) {
                edit(position);
            }
        }
    }, [focus, position, isEditing, isFocused, onSelectionChange, isCheckbox]);


    const onDoubleClick = useCallback(() => {
        if (isCheckbox || (readOnly && field.type !== FieldType.TextArea)) {
            return;
        }
        edit(position);
    }, [edit, position, readOnly, field.type, isCheckbox]);

    const confirmBeforeMultiCellPaste = useCallback((selectedCellCount: number) => {
        return confirmDialog({
            title: t("copy.clipboard.confirm.title"),
            description: translate(t, "copy.clipboard.confirm.description", [selectedCellCount]),
            cancelButtonStyle: getWsButtonStyle(wsColor),
            cancelButtonHoverStyle: getWsButtonStyle(wsDarkColor),
            confirmButtonStyle: getWsButtonStyle(wsColor),
            confirmButtonHoverStyle: getWsButtonStyle(wsDarkColor),
        });
    }, [confirmDialog, t, wsColor, wsDarkColor]);

    const confirmBeforeMultiCellClear = useCallback((selectedCellCount: number) => {
        return confirmDialog({
            title: t("copy.clipboard.clear.confirm.title"),
            description: translate(t, "copy.clipboard.clear.confirm.description", [selectedCellCount]),
            cancelButtonStyle: getWsButtonStyle(wsColor),
            cancelButtonHoverStyle: getWsButtonStyle(wsDarkColor),
            confirmButtonStyle: getWsButtonStyle(wsColor),
            confirmButtonHoverStyle: getWsButtonStyle(wsDarkColor),
        });
    }, [confirmDialog, t, wsColor, wsDarkColor]);

    const handleArrowKeyDown = (e: React.KeyboardEvent<HTMLDivElement>, position: CellPosition | null) => {
        if (!isEditing || !position) {
            e.stopPropagation();
            e.preventDefault();
            var newPosition = position;
            switch (e.key) {
                case 'ArrowUp':
                    newPosition = upPosition(position);
                    break;
                case 'ArrowDown':
                    newPosition = downPosition(position);
                    break;
                case 'ArrowLeft':
                    newPosition = leftPosition(position, colNumber);
                    break;
                case 'ArrowRight':
                    newPosition = rightPosition(position, colNumber);
                    break;
                default:
                    return; // Ignore other keys
            }
            if (newPosition && onSelectionChange) {
                onSelectionChange(newPosition, e.shiftKey, e.ctrlKey || e.metaKey);
            } else {
                focus(null);
            }
        }
    }

    function shouldReInitInput(field: Field) {
        if (field.type === FieldType.Date) {
            return false;
        }
        if (field.type === FieldType.Period) {
            const periodType = field.typeItems?.get(TYPE_ITEMS_PERIOD_TYPE);
            if (periodType === PeriodTypeDay) {
                return false;
            }
        }
        return true;
    }

    // Ignore key events that are not relevant to the grid
    const keyEventsToIgnore = ["Home", "End", "NumLock", "CapsLock", "AltGraph"];
    function ignoreKeyEvents(event: React.KeyboardEvent<HTMLDivElement>) {
        const key = event.key;
        if (key.startsWith("F") && key.length > 1) {
            return true;
        }
        if (key.startsWith("Page")) {
            return true;
        }
        if (keyEventsToIgnore.includes(key)) {
            return true;
        }
        return false;
    }

    const setValueProgrammatically = (newValue: string) => {
        if (inputRef.current) {
            // Update the value property *as if* the user typed it
            const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
                HTMLInputElement.prototype,
                'value'
            )?.set;
            nativeInputValueSetter?.call(inputRef.current, newValue);

            // Create and dispatch an 'input' event to simulate user input
            const event = new Event('input', { bubbles: true });
            inputRef.current.dispatchEvent(event);
        }
    };

    const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (!position) {
            return;
        }
        // Treat arrow keys in separated function
        if (event.key.startsWith("Arrow")) {
            handleArrowKeyDown(event, position);
            return;
        }
        switch (event.key) {
            case "Enter":
                event.preventDefault();
                if (isEditing) {
                    focus(position);
                } else {
                    if (!readOnly && !isCheckbox) {
                        edit(position);
                    }
                }
                break;
            case "Tab":
                event.preventDefault();
                let next = null;
                if (event.shiftKey) {
                    next = prevPosition(position, colNumber);
                } else {
                    next = nextPosition(position, colNumber);
                }
                if (samePosition(next, position)) {
                    saveValue();
                }
                focus(next);
                break;
            case "Escape":
                event.preventDefault();
                focus(position);
                break;
            case "Shift":
            case "Control":
                break;
            case "Backspace":
            case "Delete":
                if (!isEditing && !readOnly) {
                    event.preventDefault();
                    event.stopPropagation();
                    clearSelectedCells();
                }
                break;
            default:
                if (ignoreKeyEvents(event)) {
                    return;
                }
                if (event.ctrlKey) {
                    if (event.key === "c") {
                        // copy(localItem, field, currentAggregation, format, t, fieldByKeys ?? new Map());
                        copyAll(t);
                    } else if (event.key === "v" && !readOnly) {
                        pasteOnSelectedCells();

                    }
                    return;
                }
                if (event.key === "Alt") {
                    return;
                }
                if (!isEditing && !readOnly && !isCheckbox) {
                    // Carry the typed character into the edit session so popup
                    // cells (link) can seed their search with it; scalar cells
                    // ignore it and use setValueProgrammatically below.
                    edit(position, event.key);
                    const format = selectedView?.formats?.get(pKey ?? "") ?? field?.format;
                    if (shouldReInitInput(field) && isCompatible(event.key, field, format)) {
                        setTimeout(() => {
                            if (inputRef.current) {
                                setValueProgrammatically(event.key);
                            }
                        }, 50);
                    }
                }
                return;
        }
    };

    const pasteOnSelectedCells = useCallback(() => {
        if (readOnly) {
            return;
        }

        pasteAll(t, {
            confirmBeforeMultiCellPaste
        }).then(async (pastedItems) => {
            if (!noDataFetch && (field.type === FieldType.Link || pastedItems.length > 1)) {
                loadData();
            }
            const promises: Promise<unknown>[] = [];
            // Scalar cells are written in ONE atomic request (one undo op); links
            // and pivot cells (no recordKey) keep their per-cell path.
            const batchedValues: ItemValue[] = [];
            pastedItems.forEach(pastedItem => {
                if (field.type === FieldType.Link) {
                    promises.push(updateLink(item, pastedItem.item));
                } else if (pastedItem.item.key?.recordKey) {
                    batchedValues.push({ key: pastedItem.item.key, value: pastedItem.item.value });
                } else {
                    promises.push(updateValue(pastedItem.item, pastedItem.item.value));
                }
            });
            if (batchedValues.length > 0) {
                applyOptimisticValues(batchedValues); // immediate, immutable UI update
                promises.push(updateItemValues(batchedValues, selectedObjectInfos?.object._key));
            }
            await Promise.all(promises);
            // Reconcile (grouping/sorting/filter may change rows). Optimistic update
            // already shows the values, so this is a best-effort refresh.
            if (!noDataFetch && (field.type === FieldType.Link || pastedItems.length > 1)) {
                fetchData().then(() => {
                    stopLoadingData();
                    cellFocusStore?.triggerRefocus();
                });
            } else {
                if (batchedValues.length === 1) {
                    // Single cell pasted (this one): same refresh rules as a typed
                    // value (grouping, sorting, filters, formulas on the field).
                    afterUpdateValue(item);
                }
                cellFocusStore?.triggerRefocus();
            }
        });
    }, [readOnly, t, confirmBeforeMultiCellPaste, field.type, noDataFetch, updateLink, item, updateValue, updateItemValues, applyOptimisticValues, afterUpdateValue, selectedObjectInfos, fetchData, loadData, stopLoadingData, cellFocusStore]);

    const clearSelectedCells = useCallback(() => {
        if (readOnly) {
            return;
        }
        const selectedCellCount = Array.from(getSelectedCells().values()).reduce((count, rowCells) => count + rowCells.size, 0);
        if (selectedCellCount > 1) {
            confirmBeforeMultiCellClear(selectedCellCount).then(async (confirmed) => {
                if (confirmed) {
                    loadData();
                    updateSelectedValues(null).then(() => {
                        stopLoadingData();
                        cellFocusStore?.triggerRefocus();
                    });
                } else {
                    cellFocusStore?.triggerRefocus();
                }
            });
        } else {
            void updateSelectedValues(null);
        }
    }, [readOnly, confirmBeforeMultiCellClear, updateSelectedValues, loadData, stopLoadingData, cellFocusStore]);

    const onContextMenu = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
        if (!position || isEditing) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        const currentFocusPos = useDataStore.getState().focusPos;
        if (!containsPosition(currentFocusPos, position) && onSelectionChange) {
            onSelectionChange(position, false, false);
        }

        const menuWidth = 160;
        const menuHeight = 72;
        onContextMenuOpen?.(
            Math.min(event.clientX, window.innerWidth - menuWidth),
            Math.min(event.clientY, window.innerHeight - menuHeight),
            !readOnly,
            pasteOnSelectedCells,
            !readOnly,
            clearSelectedCells
        );
    }, [position, isEditing, onSelectionChange, onContextMenuOpen, readOnly, pasteOnSelectedCells, clearSelectedCells]);


    const saveValue = useCallback(() => {
        setBlurTriggered(Date.now());
        setIsFocused(false);

        // Save the current value if has been changed
        if (inputRef.current) {
            let inputValue: any = inputRefToValue(inputRef.current.value, field);

            if (field.type === FieldType.Period && field.typeItems?.get(TYPE_ITEMS_PERIOD_TYPE) === PeriodTypeYear) {
                const currentPeriodYear = Period.fromObject(localItem.value)?.year()?.toString() ?? "";
                const inputPeriodYear = Period.fromObject(inputValue)?.year()?.toString() ?? "";
                if (inputPeriodYear !== currentPeriodYear) {
                    if (inputValue.length === 0) {
                        void updateValue(localItem, null);
                    } else {
                        void updateValue(localItem, inputValue);
                    }
                }
            } else if (inputValue !== localItem.value) {
                void updateValue(localItem, inputValue);
            }
        }

        if (field.type !== FieldType.Link && field.type !== FieldType.Choice && field.type !== FieldType.MultiChoice && field.type !== FieldType.User && field.type !== FieldType.Period && field.type !== FieldType.TextArea) {
            setIsEditing(false);
        }
    }, [position, item, localItem, updateValue, inputRef]);

    const onBlur = useCallback((event: React.FocusEvent<HTMLDivElement>) => {
        const next = event.relatedTarget as Node | null;
        if (next && event.currentTarget.contains(next)) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();

        if (isEditing) {
            saveValue();
        }
    }, [position, item, localItem, updateValue, inputRef, isEditing]);

    return (
        <div key={divKey} className="relative w-full h-full overflow-visible">
            {showSelectionHandle &&
                <div
                    className={TW_GridCell_SelectionHandle}
                    style={selectionHandleStyle}
                    onMouseDown={(event) => {
                        event.stopPropagation();
                        event.preventDefault();
                        if (position) {
                            onSelectionHandleDragStart?.(position);
                        }
                    }}
                />
            }
            <div className={TW_GridCell + ((readOnly && field.type !== FieldType.TextArea) ? " cursor-not-allowed" : " cursor-text")} {...styleProps} style={{ ...styleProps.style }} tabIndex={0} onClick={onClick} onContextMenu={onContextMenu} ref={cellRef} onKeyDown={onKeyDown} onDoubleClick={onDoubleClick} onBlur={onBlur} >
                {renderCell(pKey, field, localItem, position, updateValue, isEditing, isFocused, inputRef, currentAggregation, blurTriggered, fieldColumn, pKey, fieldByKeys, noDataFetch)}
            </div>
        </div>
    );
}, (prevProps, newProps) => {
    // Optimized memo comparison - check most likely to change props first
    if (prevProps.position?.rowId !== newProps.position?.rowId ||
        prevProps.position?.column !== newProps.position?.column) {
        return false;
    }

    // Quick reference check for fieldColumn
    if (prevProps.fieldColumn !== newProps.fieldColumn) {
        return false;
    }

    // For items, compare the key and value instead of object reference
    const itemEqual = areItemsEqual(prevProps.item, newProps.item);
    if (!itemEqual) {
        return false;
    }

    // Additional check for noDataFetch prop
    if (prevProps.noDataFetch !== newProps.noDataFetch) {
        return false;
    }

    return true;
});

function renderCell(pathKey: string | null, field: Field, item: Item, position: CellPosition | null, updateValue: (item: Item, value: any) => void, isEditing: boolean, isFocused: boolean, inputRef: RefObject<HTMLInputElement>, currentAggregation: AggregationType, blurTriggered: number | null, fieldColumn: FieldColumn, pKey: string | null, fieldByKeys: Map<string, Field> | null, noDataFetch?: boolean) {
    // A formula which could not be computed for this line: the same error display for every type
    // (a link column keeps its own cell, since the link itself remains editable)
    if (field.type !== FieldType.Link && isFormulaError(item?.value, field, fieldByKeys)) {
        return <FormulaErrorCell key={posToString(position) + "_" + field._key} />;
    }
    const outputType = fieldToOutputType(field, currentAggregation, fieldByKeys);
    switch (outputType) {
        case FieldType.Text:
            return <TextCell key={posToString(position) + "_" + field._key} pathKey={pathKey} field={field} item={item} position={position} updateValue={updateValue} isEditing={isEditing} isFocused={isFocused} inputRef={inputRef} blurTriggered={blurTriggered} pKey={pKey} currentAggregation={currentAggregation} noDataFetch={noDataFetch} />
        case FieldType.TextArea:
            return <TextAreaCell key={posToString(position) + "_" + field._key} pathKey={pathKey} field={field} item={item} position={position} updateValue={updateValue} isEditing={isEditing} isFocused={isFocused} inputRef={inputRef} blurTriggered={blurTriggered} pKey={pKey} currentAggregation={currentAggregation} showAsBadge={false} noDataFetch={noDataFetch} />
        case FieldType.Number:
            return <NumberCell key={posToString(position) + "_" + field._key} pathKey={pathKey} field={field} item={item} position={position} updateValue={updateValue} isEditing={isEditing} isFocused={isFocused} inputRef={inputRef} blurTriggered={blurTriggered} pKey={pKey} currentAggregation={currentAggregation} noDataFetch={noDataFetch} />
        case FieldType.Boolean:
            return <CheckboxCell key={posToString(position) + "_" + field._key} pathKey={pathKey} field={field} item={item} position={position} updateValue={updateValue} isEditing={isEditing} isFocused={isFocused} inputRef={inputRef} blurTriggered={blurTriggered} pKey={pKey} currentAggregation={currentAggregation} noDataFetch={noDataFetch} />
        case FieldType.Date:
            return <DateCell key={posToString(position) + "_" + field._key} pathKey={pathKey} field={field} item={item} position={position} updateValue={updateValue} isEditing={isEditing} isFocused={isFocused} inputRef={inputRef} blurTriggered={blurTriggered} pKey={pKey} currentAggregation={currentAggregation} noDataFetch={noDataFetch} />
        case FieldType.Decimal:
            return <DecimalCell key={posToString(position) + "_" + field._key} pathKey={pathKey} field={field} item={item} position={position} updateValue={updateValue} isEditing={isEditing} isFocused={isFocused} inputRef={inputRef} blurTriggered={blurTriggered} pKey={pKey} currentAggregation={currentAggregation} noDataFetch={noDataFetch} />
        case FieldType.Percent:
            return <PercentageCell key={posToString(position) + "_" + field._key} pathKey={pathKey} field={field} item={item} position={position} updateValue={updateValue} isEditing={isEditing} isFocused={isFocused} inputRef={inputRef} blurTriggered={blurTriggered} pKey={pKey} currentAggregation={currentAggregation} noDataFetch={noDataFetch} />
        case FieldType.Devise:
            return <CurrencyCell key={posToString(position) + "_" + field._key} pathKey={pathKey} field={field} item={item} position={position} updateValue={updateValue} isEditing={isEditing} isFocused={isFocused} inputRef={inputRef} blurTriggered={blurTriggered} pKey={pKey} currentAggregation={currentAggregation} noDataFetch={noDataFetch} />
        case FieldType.Link: {
            const fieldKey = field.typeItems!.get(TYPE_ITEMS_FIELD_KEY);

            const linkedField = fieldByKeys?.get(fieldKey);
            if (linkedField?.type === FieldType.TextArea) {
                return <TextAreaCell key={posToString(position) + "_" + field._key} pathKey={pathKey} field={field} item={item} position={position} updateValue={updateValue} isEditing={isEditing} isFocused={isFocused} inputRef={inputRef} blurTriggered={blurTriggered} pKey={pKey} currentAggregation={currentAggregation} showAsBadge={true} noDataFetch={noDataFetch} />
            } else {
                return <LinkCell key={posToString(position) + "_" + field._key} pathKey={pathKey} field={field} item={item} position={position} updateValue={updateValue} isEditing={isEditing} isFocused={isFocused} inputRef={inputRef} blurTriggered={blurTriggered} pKey={pKey} currentAggregation={currentAggregation} fieldColumn={fieldColumn} noDataFetch={noDataFetch} />
            }
        }
        case FieldType.Choice:
            return <ChoiceCell key={posToString(position) + "_" + field._key} pathKey={pathKey} field={field} item={item} position={position} updateValue={updateValue} isEditing={isEditing} isFocused={isFocused} inputRef={inputRef} blurTriggered={blurTriggered} pKey={pKey} currentAggregation={currentAggregation} noDataFetch={noDataFetch} />
        case FieldType.MultiChoice:
            return <ChoiceCell key={posToString(position) + "_" + field._key} pathKey={pathKey} field={field} item={item} position={position} updateValue={updateValue} isEditing={isEditing} isFocused={isFocused} inputRef={inputRef} blurTriggered={blurTriggered} pKey={pKey} currentAggregation={currentAggregation} noDataFetch={noDataFetch} />
        case FieldType.User:
            return <UserCell key={posToString(position) + "_" + field._key} pathKey={pathKey} field={field} item={item} position={position} updateValue={updateValue} isEditing={isEditing} isFocused={isFocused} inputRef={inputRef} blurTriggered={blurTriggered} pKey={pKey} currentAggregation={currentAggregation} noDataFetch={noDataFetch} />
        case FieldType.Period:
            return <PeriodCell key={posToString(position) + "_" + field._key} pathKey={pathKey} field={field} item={item} position={position} updateValue={updateValue} isEditing={isEditing} isFocused={isFocused} inputRef={inputRef} blurTriggered={blurTriggered} pKey={pKey} currentAggregation={currentAggregation} noDataFetch={noDataFetch} />
        case FieldType.Url:
            return <UrlCell key={posToString(position) + "_" + field._key} pathKey={pathKey} field={field} item={item} position={position} updateValue={updateValue} isEditing={isEditing} isFocused={isFocused} inputRef={inputRef} blurTriggered={blurTriggered} pKey={pKey} currentAggregation={currentAggregation} noDataFetch={noDataFetch} />
        case FieldType.Button:
            return <ButtonCell key={posToString(position) + "_" + field._key} pathKey={pathKey} field={field} item={item} position={position} updateValue={updateValue} isEditing={isEditing} isFocused={isFocused} inputRef={inputRef} blurTriggered={blurTriggered} pKey={pKey} currentAggregation={currentAggregation} noDataFetch={noDataFetch} />
        default:
            return <div key={posToString(position) + "_" + field._key} className="GridCell">{item ? item.value : ""}</div>
    }
}
